/**
 * What to say when the failure never reached the app's own envelope.
 *
 * axios' own `error.message` is written for a developer reading a stack
 * trace — "Request failed with status code 429" was being shown verbatim
 * on the sign-in screen, which tells the person locked out neither what
 * happened nor what to do about it. Every status the API can plausibly
 * answer with gets a sentence in the interface's voice instead; anything
 * unlisted falls back to the generic one rather than to axios' text.
 */
export const STATUS_MESSAGES = {
    0: 'We could not reach the server. Check your connection and try again.',
    400: 'That request could not be processed. Please check your details and try again.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have permission to do that.',
    404: 'We could not find what you were looking for.',
    405: 'That action is not available.',
    408: 'The server took too long to answer. Please try again.',
    413: 'That file is too large to upload. Please choose a smaller file.',
    419: 'Your session has expired. Please sign in again.',
    422: 'Some of the details you entered are not valid. Please check them and try again.',
    429: 'Too many attempts. Please wait a moment and try again.',
    500: 'Something went wrong on our end. Please try again in a moment.',
    502: 'The server is not responding right now. Please try again in a moment.',
    503: 'The service is temporarily unavailable. Please try again in a moment.',
    504: 'The server took too long to answer. Please try again.',
};

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';

/**
 * "Too many attempts" is more useful with the wait attached, and Laravel's
 * throttle middleware always sends one on a 429.
 */
const retryAfterSentence = (response) => {
    const seconds = parseInt(response && response.headers && response.headers['retry-after'], 10);
    if (! seconds || Number.isNaN(seconds) || seconds <= 0) {
        return '';
    }
    if (seconds < 60) {
        return ` Please try again in ${seconds} second${seconds === 1 ? '' : 's'}.`;
    }
    const minutes = Math.ceil(seconds / 60);
    return ` Please try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
};

/**
 * The sentence to show for a failure that carried no message of the
 * app's own.
 */
export const httpErrorMessage = (status, response) => {
    if (status === 429) {
        return 'Too many attempts.'
            + (retryAfterSentence(response) || ' Please wait a moment and try again.');
    }

    return STATUS_MESSAGES[status] || GENERIC_MESSAGE;
};
